<template>
  <footer class="app-footer">
    <div class="footer-decoration">
      <div class="deco-wave"></div>
    </div>
    <div class="footer-container">
      <!-- 主要内容区 -->
      <div class="footer-main">
        <!-- Logo 和简介 -->
        <div class="footer-brand">
          <div class="brand-logo">
            <div class="logo-icon">
              <el-icon :size="32" color="white"><Monitor /></el-icon>
            </div>
            <div class="logo-text">
              <span class="brand-name">百炼谷</span>
              <span class="brand-tagline">百炼谷学习平台</span>
            </div>
          </div>
          <p class="brand-desc">专注GESP、CSP-J/S、NOIP信息学竞赛训练，让每个孩子都能享受编程的乐趣</p>
          <div class="brand-stats">
            <div class="stat-item">
              <el-icon :size="20" color="#fbbf24"><StarFilled /></el-icon>
              <span>已帮助 10000+ 学员</span>
            </div>
            <div class="stat-item">
              <el-icon :size="20" color="#34d399"><CircleCheck /></el-icon>
              <span>通过率提升 85%</span>
            </div>
          </div>
        </div>

        <!-- 链接分组 -->
        <div class="footer-links">
          <div class="link-group">
            <h4 class="group-title">
              <span class="title-icon icon-blue"><el-icon><Reading /></el-icon></span>
              学习
            </h4>
            <ul class="link-list">
              <li><router-link to="/courses" class="link-item">课程中心</router-link></li>
              <li><router-link to="/problems" class="link-item">题库练习</router-link></li>
              <li><router-link to="/contests" class="link-item">竞赛活动</router-link></li>
              <li><router-link to="/ranking" class="link-item">排行榜</router-link></li>
            </ul>
          </div>

          <div class="link-group">
            <h4 class="group-title">
              <span class="title-icon icon-green"><el-icon><Collection /></el-icon></span>
              资源
            </h4>
            <ul class="link-list">
              <li><a href="#" class="link-item" @click.prevent="showMessage('新手指南')">新手指南</a></li>
              <li><a href="#" class="link-item" @click.prevent="showMessage('语法教程')">语法教程</a></li>
              <li><a href="#" class="link-item" @click.prevent="showMessage('算法入门')">算法入门</a></li>
              <li><a href="#" class="link-item" @click.prevent="showMessage('视频课程')">视频课程</a></li>
            </ul>
          </div>

          <div class="link-group">
            <h4 class="group-title">
              <span class="title-icon icon-orange"><el-icon><Service /></el-icon></span>
              帮助
            </h4>
            <ul class="link-list">
              <li><a href="#" class="link-item" @click.prevent="showMessage('常见问题')">常见问题</a></li>
              <li><a href="#" class="link-item" @click.prevent="showMessage('联系我们')">联系我们</a></li>
              <li><router-link to="/feedback" class="link-item">意见反馈</router-link></li>
              <li><a href="#" class="link-item" @click.prevent="showMessage('用户协议')">用户协议</a></li>
            </ul>
          </div>
        </div>
      </div>

      <!-- 分隔线 -->
      <div class="footer-divider">
        <div class="divider-line"></div>
        <div class="divider-dots">
          <span class="dot dot-blue"></span>
          <span class="dot dot-green"></span>
          <span class="dot dot-orange"></span>
          <span class="dot dot-pink"></span>
        </div>
        <div class="divider-line"></div>
      </div>

      <!-- 底部信息 -->
      <div class="footer-bottom">
        <div class="copyright">
          <span class="copyright-text">© 2026 百炼谷 百炼谷平台</span>
          <span class="copyright-divider">|</span>
          <span class="copyright-slogan">让编程成为孩子的超能力</span>
        </div>
        <div class="social-links">
          <a href="#" class="social-item wechat" @click.prevent="showMessage('微信公众号')" title="微信">
            <el-icon><ChatDotRound /></el-icon>
          </a>
          <a href="#" class="social-item qq" @click.prevent="showMessage('QQ群')" title="QQ">
            <el-icon><Message /></el-icon>
          </a>
          <a href="#" class="social-item email" @click.prevent="showMessage('邮箱')" title="邮箱">
            <el-icon><MessageBox /></el-icon>
          </a>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { ElMessage } from 'element-plus'
import { 
  Monitor, 
  StarFilled, 
  CircleCheck, 
  Reading, 
  Collection, 
  Service,
  ChatDotRound, 
  Message, 
  MessageBox 
} from '@element-plus/icons-vue'

const showMessage = (content: string) => {
  ElMessage.info(`${content} 功能开发中`)
}
</script>

<style scoped lang="scss">
.app-footer {
  position: relative;
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  color: #94a3b8;
  padding: 80px 0 32px;
  margin-top: auto;
}

.footer-decoration {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  overflow: hidden;

  .deco-wave {
    position: absolute;
    top: -30px;
    left: 0;
    right: 0;
    height: 60px;
    background: white;
    border-radius: 0 0 50% 50%;
  }
}

.footer-container {
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 24px;
}

// 主要内容区
.footer-main {
  display: grid;
  grid-template-columns: 1.5fr 2fr;
  gap: 80px;
  margin-bottom: 48px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 48px;
  }
}

// 品牌区域
.footer-brand {
  .brand-logo {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 20px;

    .logo-icon {
      width: 56px;
      height: 56px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border-radius: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3);
    }

    .logo-text {
      display: flex;
      flex-direction: column;

      .brand-name {
        font-size: 28px;
        font-weight: 800;
        color: white;
        line-height: 1.2;
      }

      .brand-tagline {
        font-size: 14px;
        color: #94a3b8;
        font-weight: 500;
      }
    }
  }

  .brand-desc {
    font-size: 15px;
    line-height: 1.8;
    color: #cbd5e1;
    margin-bottom: 24px;
    max-width: 400px;
  }

  .brand-stats {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .stat-item {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 14px;
      color: #e2e8f0;
      font-weight: 500;
    }
  }
}

// 链接分组
.footer-links {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;

  @media (max-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.link-group {
  .group-title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 16px;
    font-weight: 700;
    color: white;
    margin-bottom: 20px;

    .title-icon {
      width: 32px;
      height: 32px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;

      &.icon-blue {
        background: rgba(59, 130, 246, 0.2);
        color: #60a5fa;
      }

      &.icon-green {
        background: rgba(16, 185, 129, 0.2);
        color: #34d399;
      }

      &.icon-orange {
        background: rgba(245, 158, 11, 0.2);
        color: #fbbf24;
      }
    }
  }
}

.link-list {
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    margin-bottom: 12px;
  }
}

.link-item {
  color: #94a3b8;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &:hover {
    color: white;
    transform: translateX(4px);
  }

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #64748b;
    transition: all 0.3s ease;
  }

  &:hover::before {
    background: #60a5fa;
  }
}

// 分隔线
.footer-divider {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;

  .divider-line {
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  }

  .divider-dots {
    display: flex;
    gap: 8px;

    .dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;

      &.dot-blue { background: #60a5fa; }
      &.dot-green { background: #34d399; }
      &.dot-orange { background: #fbbf24; }
      &.dot-pink { background: #f472b6; }
    }
  }
}

// 底部信息
.footer-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 20px;
    text-align: center;
  }
}

.copyright {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 8px;
  }

  .copyright-text {
    color: #64748b;
  }

  .copyright-divider {
    color: #475569;

    @media (max-width: 640px) {
      display: none;
    }
  }

  .copyright-slogan {
    color: #94a3b8;
    font-weight: 500;
  }
}

.social-links {
  display: flex;
  gap: 12px;
}

.social-item {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.1);
  color: #94a3b8;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    color: white;
  }

  &.wechat:hover {
    background: #07c160;
  }

  &.qq:hover {
    background: #12b7f5;
  }

  &.email:hover {
    background: #ea4335;
  }
}
</style>
